import type { SymbolPreviewItem } from '@/components/SymbolsPreview';
import type { SymbolKey } from '@/entities/symbol/types';
import type { AIMessage, Message } from '@/features/AIChat/context/utils';
import isHumanMessage from '@/features/AIChat/utils/isHumanMessage';
import { takeItemBasedOnOrder } from '@/pages/Catalog/utils/takeItemBasedOnOrder';

export const GX_REGEX = /GX\d{7}/g;

const extractCodesFromPart = (part: AIMessage['content'][number]): string[] => {
  if (part.kind === 'text') {
    return part.text.match(GX_REGEX) ?? [];
  }
  if (part.kind === 'tool_result') {
    return part.rows.map(row => row.code?.trim()).filter(Boolean);
  }
  return [];
};

export function extractGxCodesFromMessage(msg: Message): string[] {
  if (isHumanMessage(msg)) return [];
  try {
    const raw = msg.content.flatMap(extractCodesFromPart);
    return [...new Set(raw)];
  } catch (e) {
    console.error('Error extracting GX codes from message:', e);
  }
  return [];
}

type CatalogueApiItem = {
  Code: string;
  PeriodType: string;
  Period?: string;
  TimeRef: string;
  Alias?: string;
  Currency?: string;
  Units?: string;
  Title?: string;
};

const EMPTY_SYMBOL: SymbolKey = { Code: '', PeriodType: '', Period: '', TimeRef: '' };

export function toCatalogueItem(dataArr?: CatalogueApiItem[]): SymbolPreviewItem {
  if (!dataArr || dataArr.length === 0) {
    return {
      symbol: EMPTY_SYMBOL,
      label: '',
      metadata: { Currency: '', Units: '', Title: '' }
    };
  }

  const bestKeyPerCode = dataArr.reduce<Record<string, SymbolKey>>((acc, data) => {
    const key: SymbolKey = {
      Code: data.Code,
      PeriodType: data.PeriodType,
      Period: data.Period ?? '1',
      TimeRef: data.TimeRef
    };
    const existing = acc[key.Code];
    acc[key.Code] = existing ? takeItemBasedOnOrder(existing, key) : key;
    return acc;
  }, {});

  const { Alias, Currency = '', Units = '', Title = '' } = dataArr[0];

  return {
    symbol: Object.values(bestKeyPerCode)[0],
    label: '',
    metadata: { Alias, Currency, Units, Title }
  };
}

export function gxQueryKey(code: string) {
  return ['gxCodeAIChat', code] as const;
}
